import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

const AuditSummaryModal = ({ dateRange, onClose }) => {
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [copied, setCopied] = useState(false);
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        const fetchSummary = async () => {
            try {
                setLoading(true);
                const response = await api.get('/cash-box/audit-summary', {
                    params: {
                        from_date: dateRange.from,
                        to_date: dateRange.to
                    }
                });
                setSummary(response.data);
            } catch (error) {
                console.error('Error fetching audit summary:', error);
            } finally {
                setLoading(false);
            }
        };

        if (dateRange.from && dateRange.to) {
            fetchSummary();
        }
    }, [dateRange]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(Math.round(amount || 0));
    };

    const copyLink = () => {
        const url = new URL(window.location.href);
        url.searchParams.set('tab', 'audit');
        url.searchParams.set('report', 'true');
        url.searchParams.set('from', dateRange.from);
        url.searchParams.set('to', dateRange.to);
        
        navigator.clipboard.writeText(url.toString());
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handlePrint = () => {
        window.print();
    };

    const handleDownloadPdf = async () => {
        try {
            setDownloading(true);
            const response = await api.get('/cash-box/audit-summary/pdf', {
                params: {
                    from_date: dateRange.from,
                    to_date: dateRange.to
                },
                responseType: 'blob'
            });
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `auditoria_caja_${dateRange.from}_a_${dateRange.to}.pdf`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error('Error downloading PDF:', error);
            alert('Error generando el PDF. Intenta con Imprimir / PDF.');
        } finally {
            setDownloading(false);
        }
    };

    if (loading) {
        return (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]">
                <Card className="w-full max-w-lg">
                    <CardContent className="p-10 text-center">
                        Generando informe analítico...
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (!summary) return null;

    const { summary: stats, topFails, largeMovements } = summary;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4 overflow-y-auto print:p-0 print:bg-white print:relative">
            <Card className="w-full max-w-4xl bg-white shadow-2xl print:shadow-none print:w-full">
                <CardHeader className="flex flex-row items-center justify-between border-b pb-4 print:hidden">
                    <CardTitle className="text-xl">📜 Informe de Auditoría de Caja</CardTitle>
                    <div className="flex gap-2">
                        <button 
                            onClick={copyLink}
                            className="bg-blue-50 text-blue-600 px-3 py-1 rounded border border-blue-200 hover:bg-blue-100 text-sm font-medium transition-all"
                        >
                            {copied ? '✅ Link Copiado' : '🔗 Copiar Link'}
                        </button>
                        <button 
                            onClick={handleDownloadPdf}
                            disabled={downloading}
                            className="bg-green-50 text-green-700 px-3 py-1 rounded border border-green-200 hover:bg-green-100 text-sm font-medium transition-all disabled:opacity-50"
                        >
                            {downloading ? '⏳ Generando...' : '📥 Descargar PDF'}
                        </button>
                        <button 
                            onClick={handlePrint}
                            className="bg-gray-100 text-gray-700 px-3 py-1 rounded hover:bg-gray-200 text-sm font-medium"
                        >
                            🖨️ Imprimir
                        </button>
                        <button 
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 text-2xl font-bold ml-2"
                        >
                            &times;
                        </button>
                    </div>
                </CardHeader>
                
                {/* Printable Content */}
                <CardContent className="p-6 space-y-8 print:p-0">
                    <div className="text-center space-y-2 border-b pb-6">
                        <h1 className="text-2xl font-bold text-gray-900 uppercase">Resumen de Auditoría de Caja</h1>
                        <p className="text-gray-500 font-medium tracking-wide">
                            Periodo: {dateRange.from} al {dateRange.to}
                        </p>
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-green-50 p-6 rounded-xl border border-green-100 shadow-sm">
                            <label className="text-xs font-bold text-green-700 uppercase tracking-widest mb-1 block">Total Sobrantes</label>
                            <span className="text-2xl font-black text-green-800">{formatCurrency(stats.total_positive)}</span>
                            <div className="mt-2 text-[10px] text-green-600 font-medium">Dinero declarado por encima de lo esperado</div>
                        </div>
                        <div className="bg-red-50 p-6 rounded-xl border border-red-100 shadow-sm">
                            <label className="text-xs font-bold text-red-700 uppercase tracking-widest mb-1 block">Total Faltantes</label>
                            <span className="text-2xl font-black text-red-800">{formatCurrency(stats.total_negative)}</span>
                            <div className="mt-2 text-[10px] text-red-600 font-medium">Dinero faltante según registros del sistema</div>
                        </div>
                        <div className="bg-slate-900 p-6 rounded-xl border border-slate-800 shadow-lg sm:col-span-1 md:col-span-1">
                            <label className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-1 block">Balance Neto</label>
                            <span className={`text-2xl font-black ${stats.net_difference >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                {formatCurrency(stats.net_difference)}
                            </span>
                            <div className="mt-2 text-[10px] text-slate-500 font-medium">Exposición financiera total del periodo</div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-8">
                        {/* Top Problematic */}
                        <div className="space-y-4">
                            <h3 className="font-black text-sm text-gray-700 border-l-4 border-blue-600 pl-3 uppercase tracking-wider">Cajas con Mayores Descuadres</h3>
                            <div className="border rounded-xl overflow-hidden shadow-sm">
                                <table className="w-full text-xs">
                                    <thead className="bg-gray-50 border-b">
                                        <tr>
                                            <th className="p-3 text-left font-bold text-gray-500 uppercase tracking-tighter">Caja #</th>
                                            <th className="p-3 text-right font-bold text-gray-500 uppercase tracking-tighter">Diferencia</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y">
                                        {topFails.map(f => (
                                            <tr key={f.id} className="hover:bg-gray-50">
                                                <td className="p-3 font-semibold text-gray-700">#{f.session_number} ({new Date(f.opened_at).toLocaleDateString()})</td>
                                                <td className={`p-3 text-right font-black ${f.difference > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                    {formatCurrency(f.difference)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Large Expenses */}
                        <div className="space-y-4">
                            <h3 className="font-black text-sm text-gray-700 border-l-4 border-orange-500 pl-3 uppercase tracking-wider">Gastos Mayores a $500k</h3>
                            <div className="border rounded-xl overflow-hidden shadow-sm">
                                <table className="w-full text-xs">
                                    <thead className="bg-gray-50 border-b">
                                        <tr>
                                            <th className="p-3 text-left font-bold text-gray-500 uppercase tracking-tighter">Concepto</th>
                                            <th className="p-3 text-right font-bold text-gray-500 uppercase tracking-tighter">Monto</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y">
                                        {largeMovements.map(m => (
                                            <tr key={m.id} className="hover:bg-gray-50">
                                                <td className="p-3 text-gray-700 font-medium truncate max-w-[150px]">{m.description}</td>
                                                <td className="p-3 text-right font-bold text-orange-600">{formatCurrency(m.amount)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    <div className="bg-gray-50 p-6 rounded-xl border border-dashed border-gray-300 mt-8">
                        <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-3">Recomendaciones de Seguridad</h4>
                        <ul className="text-xs text-gray-600 space-y-2 list-disc pl-5 font-medium">
                            <li>Verificar que las <strong>Transferencias Bancarias</strong> NO se sumen al cierre de efectivo manual.</li>
                            <li>Asegurar que cada gasto operativo de caja tenga un soporte digital o físico verificable.</li>
                            <li>Revisar los descuadres superiores a $50.000 con la cajera el mismo día del cierre.</li>
                        </ul>
                    </div>
                    
                    <div className="hidden print:block pt-20 border-t mt-20">
                        <div className="flex justify-around text-center">
                            <div className="w-48 border-t pt-2 text-xs font-bold uppercase text-gray-400">Firma Administrador</div>
                            <div className="w-48 border-t pt-2 text-xs font-bold uppercase text-gray-400">Firma Cajera</div>
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};

export default AuditSummaryModal;
